import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { Label } from 'lib/Label';
import { CodeNameModel } from 'components/common/types';
import { Store } from 'components/common/types/Store.types';
import SectionDivider from 'components/pages/Planner/SectionDivider';
import { FormatOptions } from 'components/common/FormatOptions/FormatOptions';
import { useCategoryOptions } from 'customHooks/useCategoryOptions';
import { canDisplayOptionResults } from 'components/pages/Planner/PlannerSecondaryPanel/utils';
import Search from 'lib/Search';
import SelectionPermissionDropdown from '../SelectionPermissionDropdown/SelectionPermissionDropdown';
import { permissionOptions } from '../../consts';
import { isValueRequired } from '../../utils/isValueRequired';
import { isPanelConfirmDisabled } from '../../utils/isPanelConfirmDisabled';
import { ProductFormatsPanelProps } from './ProductFormatsPanelProps.types';
import { secondaryPanelLayoutStyles } from '../../styles';
import { ProductSecondaryPanel } from '../ProductSecondaryPanel/ProductSecondaryPanel';

export const ProductFormatsPanel: React.FC<ProductFormatsPanelProps> = ({
  onConfirm,
  onCancel,
  isReadOnly = false,
}) => {
  const productFormats = useSelector((state: Store) => state.productManagement.newProduct?.formats);
  const defaultSelectedProductFormats =
    permissionOptions.find(({ code }) => code === productFormats?.selectionPermission) ?? permissionOptions[0];
  const [selectedPermission, setSelectedPermission] = useState<CodeNameModel>(defaultSelectedProductFormats);
  const formatOptions = useSelector(
    (state: Store) => state.userOptions.referenceOptions.format as unknown as Record<string, CodeNameModel[]>,
  );

  const initialSelectedFormats = useMemo(() => productFormats?.value ?? [], [productFormats?.value]);

  const {
    onCategoryDirectionSort,
    searchText,
    openCategories,
    onFilterTextChange,
    selectedOptions,
    filteredOptions,
    getNumberOfFilteredOptions,
    sortDirection,
    includedOptions,
    excludedOptions,
    isOptionSelected,
    isOptionIncluded,
    onOptionIncludeExcludeChange,
    onToggleCategory,
    setSelectedOptions,
    onCategoryIncludeAll,
    onCategoryExcludeAll,
    getCategoryIncludeState,
  } = useCategoryOptions({ options: formatOptions, initialSelectedOptions: initialSelectedFormats });

  const isChange = useMemo(
    () => !isEqual(initialSelectedFormats, selectedOptions),
    [initialSelectedFormats, selectedOptions],
  );

  return (
    <ProductSecondaryPanel
      title="Format"
      isDisabled={
        isPanelConfirmDisabled({
          field: productFormats,
          selectedPermission,
          newFieldValue: selectedOptions.length ? selectedOptions : undefined,
        }) || isReadOnly
      }
      onSubmit={() => onConfirm({ selectedPermission, formats: selectedOptions })}
      onCancel={onCancel}
      isChange={isChange}
      customStyles={secondaryPanelLayoutStyles}
    >
      <SelectionPermissionDropdown
        selectedPermission={selectedPermission}
        setSelectedPermission={setSelectedPermission}
      />

      {isValueRequired(selectedPermission.code) && (
        <>
          <div className="my-4">
            <SectionDivider />
          </div>

          <Label label="Select formats" />

          <div className="mb-2">
            <Search value={searchText} placeholder="Search formats" onChange={onFilterTextChange} />
          </div>

          {canDisplayOptionResults(filteredOptions) ? (
            <FormatOptions
              filteredOptions={filteredOptions}
              selectedFormats={selectedOptions}
              onCategoryDirectionSort={onCategoryDirectionSort}
              sortDirection={sortDirection}
              onToggleCategory={onToggleCategory}
              setSelectedOptions={setSelectedOptions}
              getNumberOfFilteredOptions={getNumberOfFilteredOptions}
              isOptionIncluded={isOptionIncluded}
              isOptionSelected={isOptionSelected}
              onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
              openCategories={openCategories}
              includedOptions={includedOptions}
              excludedOptions={excludedOptions}
              onCategoryIncludeAll={onCategoryIncludeAll}
              onCategoryExcludeAll={onCategoryExcludeAll}
              getCategoryIncludeState={getCategoryIncludeState}
            />
          ) : (
            <p>There are no results matching your search</p>
          )}
        </>
      )}
    </ProductSecondaryPanel>
  );
};
